import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { clampQuantity } from "../lib/format.js";

const STORAGE_KEY = "ecotimbre-cart-v2";
const CartContext = createContext(null);

function loadCart() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  function addItem(product, variant, quantity = 1) {
    const key = `${product.slug}:${variant.key}`;
    setItems((current) => {
      const existing = current.find((item) => item.key === key);
      if (existing) {
        return current.map((item) =>
          item.key === key ? { ...item, quantity: clampQuantity(item.quantity + quantity) } : item
        );
      }
      return [
        ...current,
        {
          key,
          productSlug: product.slug,
          productName: product.name,
          image: product.image,
          imageAlt: product.imageAlt,
          variant,
          quantity: clampQuantity(quantity),
        },
      ];
    });
  }

  function updateQuantity(key, quantity) {
    setItems((current) => current.map((item) => (item.key === key ? { ...item, quantity: clampQuantity(quantity) } : item)));
  }

  function removeItem(key) {
    setItems((current) => current.filter((item) => item.key !== key));
  }

  function clearCart() {
    setItems([]);
  }

  const value = useMemo(() => {
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    const subtotal = Number(items.reduce((sum, item) => sum + item.variant.price * item.quantity, 0).toFixed(2));
    const regularSubtotal = Number(
      items.reduce((sum, item) => sum + item.variant.regularPrice * item.quantity, 0).toFixed(2)
    );
    const shipping = itemCount === 0 || subtotal >= 40 ? 0 : 2.9;
    const total = Number((subtotal + shipping).toFixed(2));
    const savings = Number((regularSubtotal - subtotal).toFixed(2));
    return {
      items,
      itemCount,
      subtotal,
      regularSubtotal,
      shipping,
      total,
      savings,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
    };
  }, [items]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart doit être utilisé dans CartProvider");
  return context;
}
